import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { LogOut, RefreshCw, Database, Eye, EyeOff, ClipboardList, Lightbulb, DollarSign, Printer, LayoutDashboard, CheckSquare, FileText, FolderKanban, Menu, X, Users, BarChart3, ArrowLeft, Upload, FileSpreadsheet, MessageSquare, Image, FolderOpen, Search, Command, Activity } from 'lucide-react';
import StockTable from './StockTable';
import Orders from './Orders';
import ProductPlanning from './ProductPlanning';
import PriceList from './PriceList';
import Dashboard from './Dashboard';
import ToDo from './ToDo';
import Accounts from './Accounts';
import ChatWindow from './ChatWindow';
import NewProjects from './NewProjects';
import QuickActions from './QuickActions';
import KeyboardShortcuts from './KeyboardShortcuts';
import UserManagement from './UserManagement';
import Analytics from './Analytics';
import Messaging from './Messaging';
import MobileBottomNav from './MobileBottomNav';
import MediaLibrary from './MediaLibrary';
import DocumentLibrary from './DocumentLibrary';
import ActivityFeed from './ActivityFeed';
import GlobalSearch from './GlobalSearch';
import PullToRefresh from './PullToRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [activeTab, setActiveTab] = useState('merged');
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  
  // WINLIFE Upload State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // WINLIFE View Sheet State
  const [winlifeStocks, setWinlifeStocks] = useState([]);
  const [winlifeLoading, setWinlifeLoading] = useState(false);

  // Global Search keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowGlobalSearch(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle navigation from global search
  const handleSearchNavigate = (type, item) => {
    switch (type) {
      case 'product':
        setActiveTab('merged');
        setSearchTerm(item.product_name);
        break;
      case 'order':
        setActiveTab('orders');
        break;
      case 'supplier':
        setActiveTab('accounts');
        break;
      case 'document':
        setActiveTab('documents');
        break;
      default:
        break;
    }
  };

  // Pull-to-refresh handler: re-fetch data based on current tab
  const handleMobileRefresh = async () => {
    try {
      if (activeTab === 'merged') {
        await fetchStocks();
      } else {
        // For other tabs (dashboard, orders, accounts, etc.), broadcast a refresh event
        // that child components listen for via window event.
        window.dispatchEvent(new CustomEvent('app:refresh-data', { detail: { tab: activeTab } }));
        // Small delay so the pull-to-refresh animation feels real
        await new Promise((res) => setTimeout(res, 600));
      }
    } catch (e) {
      // silently ignore - child components handle their own errors
    }
  };

  // Helper function to check if user has access to a panel
  const hasAccess = (panel) => {
    // Admin always has access
    if (user.role === 'admin') return true;
    // Check user permissions
    return user.permissions?.[panel] !== false;
  };
  
  // WINLIFE Upload Functions
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (validExtensions.includes(fileExtension)) {
        setUploadFile(selectedFile);
        toast.success(`File "${selectedFile.name}" selected`);
      } else {
        toast.error('Please select a valid file (.xlsx, .xls, or .csv)');
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await axios.post(`${API}/upload/winlife`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      toast.success(response.data.message);
      setUploadFile(null);
      if (document.getElementById('winlife-file-input')) {
        document.getElementById('winlife-file-input').value = null;
      }
      
      // Switch to merged stock view and refresh
      setActiveTab('merged');
      await fetchStocks();
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      toast.error(`Upload error: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  // Fetch WINLIFE-specific stocks for View Sheet panel
  const fetchWinlifeStocks = async () => {
    setWinlifeLoading(true);
    try {
      const response = await axios.get(`${API}/stock/winlife`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      setWinlifeStocks(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch WINLIFE stock data');
    } finally {
      setWinlifeLoading(false);
    }
  };

  // WINLIFE View Sheet columns
  const winlifeColumns = [
    { key: 'product_name', label: 'Product Name', sortable: true, type: 'text', minWidth: '150px' },
    { key: 'packing', label: 'Packing', sortable: true, type: 'text', minWidth: '100px' },
    { key: 'available_qty', label: 'Available Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'manufactured_by', label: 'Manufactured By', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'expiry_date', label: 'Expiry Date', sortable: true, type: 'text', minWidth: '100px' },
    { key: 'order_given_to', label: 'Order Given To', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'order_qty', label: 'Order Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'on_date', label: 'On Date', sortable: true, type: 'text', minWidth: '100px' },
    { key: 'cost', label: 'Cost @ WINLIFE', sortable: true, type: 'number', minWidth: '110px', hidden: !showCost },
  ];

  // Handle keyboard shortcuts
  const handleShortcut = useCallback((action) => {
    switch (action) {
      case 'dashboard':
        setActiveTab('dashboard');
        setMobileMenuOpen(false);
        break;
      case 'stock':
        setActiveTab('merged');
        setMobileMenuOpen(false);
        break;
      case 'orders':
        setActiveTab('orders');
        setMobileMenuOpen(false);
        break;
      case 'todo':
        setActiveTab('todo');
        setMobileMenuOpen(false);
        break;
      case 'pricelist':
        setActiveTab('pricelist');
        setMobileMenuOpen(false);
        break;
      case 'refresh':
        if (activeTab === 'merged') {
          // Trigger refresh - will be handled by fetchStocks
          window.dispatchEvent(new CustomEvent('refresh-data'));
        }
        toast.info('Refreshing data...');
        break;
      case 'escape':
        setMobileMenuOpen(false);
        break;
      default:
        break;
    }
  }, [activeTab]);

  // Handle quick action from FAB
  const handleQuickAction = (actionId) => {
    handleShortcut(actionId);
  };

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/merged`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      setStocks(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch stock data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'merged') {
      fetchStocks();
    }
    if (activeTab === 'viewsheet' && user.role === 'winlife') {
      fetchWinlifeStocks();
    }
  }, [activeTab]);

  const saveOrderFields = async (productName, orderData) => {
    try {
      await axios.put(`${API}/stock/update-order-fields`, 
        {
          product_name: productName,
          order_given_to: orderData.order_given_to,
          order_qty: orderData.order_qty,
          on_date: orderData.on_date
        },
        {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        }
      );
      toast.success('Order details saved and reflected in Orders tab');
      fetchStocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order details');
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!window.confirm('Remove duplicate entries from merged stock? This will keep only unique product+expiry combinations.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/remove-duplicates`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success(response.data.message);
      fetchStocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove duplicates');
    } finally {
      setLoading(false);
    }
  };

  const handleWipeMergedStock = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL merged stock data and recreate it from SRH and WINLIFE sources.\n\n✅ Your ACTIVE ORDERS will NOT be affected.\n✅ SRH and WINLIFE stock data will NOT be affected.\n\nThis only refreshes the merged view.\n\nAre you sure?')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/wipe-merged-stock`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success(response.data.message);
      fetchStocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to wipe merged stock');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshMergedStock = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/refresh-merged-stock`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success(response.data.message);
      fetchStocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh merged stock');
    } finally {
      setLoading(false);
    }
  };

  const filteredStocks = stocks.filter(stock => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      stock.product_name?.toLowerCase().includes(search) ||
      stock.manufactured_by?.toLowerCase().includes(search) ||
      stock.order_given_to?.toLowerCase().includes(search)
    );
  });

  const handlePrint = () => {
    window.print();
  };

  const columns = [
    { key: 'product_name', label: 'Product Name', sortable: true, type: 'text', minWidth: '150px' },
    { key: 'qty_srh', label: 'Qty @ SRH', sortable: true, type: 'number', minWidth: '90px' },
    { key: 'qty_winlife', label: 'Qty @ WINLIFE', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'merged_quantity', label: 'Merged Qty', sortable: true, type: 'number', minWidth: '100px' },
    { key: 'sale_qty_last_month', label: 'Sale (Last Month)', sortable: true, type: 'number', minWidth: '100px' },
    { 
      key: 'available_qty_months', 
      label: 'Available Qty (Months)', 
      sortable: true, 
      type: 'number', 
      minWidth: '110px',
      format: (val) => val ? val.toFixed(2) : '0.00'
    },
    { key: 'reorder_3_months', label: 'Reorder 3M', sortable: true, type: 'number', minWidth: '90px' },
    { key: 'reorder_6_months', label: 'Reorder 6M', sortable: true, type: 'number', minWidth: '90px' },
    { key: 'reorder_9_months', label: 'Reorder 9M', sortable: true, type: 'number', minWidth: '90px' },
    { key: 'manufactured_by', label: 'Manufactured By', sortable: true, type: 'text', minWidth: '130px' },
    { key: 'order_given_to', label: 'Order Given To', sortable: true, type: 'text', minWidth: '130px', editableField: true },
    { key: 'order_qty', label: 'Order Qty', sortable: true, type: 'number', minWidth: '90px', editableField: true },
    { key: 'on_date', label: 'On Date', sortable: true, type: 'text', minWidth: '100px', editableField: true },
    { key: 'expiry_date', label: 'Expiry Date', sortable: true, type: 'text', minWidth: '100px' },
    { 
      key: 'cost_srh', 
      label: 'Cost @ SRH', 
      sortable: true, 
      type: 'number', 
      minWidth: '100px', 
      hidden: !showCost 
    },
    { 
      key: 'cost_winlife', 
      label: 'Cost @ WINLIFE', 
      sortable: true, 
      type: 'number', 
      minWidth: '110px', 
      hidden: !showCost 
    },
    { 
      key: 'value_srh', 
      label: 'Value @ SRH', 
      sortable: true, 
      type: 'number', 
      minWidth: '100px', 
      hidden: !showCost,
      format: (val) => val ? val.toFixed(2) : '0.00'
    },
    { 
      key: 'value_winlife', 
      label: 'Value @ WINLIFE', 
      sortable: true, 
      type: 'number', 
      minWidth: '110px', 
      hidden: !showCost,
      format: (val) => val ? val.toFixed(2) : '0.00'
    },
    { 
      key: 'total_value', 
      label: 'Total Value', 
      sortable: true, 
      type: 'number', 
      minWidth: '140px',
      hidden: !showCost,
      render: (item) => {
        const total = (item.value_srh || 0) + (item.value_winlife || 0);
        return (
          <div className="flex items-center justify-start">
            <span className="font-bold text-blue-600 text-sm px-2 py-1 bg-blue-50 rounded">
              ₹ {total.toFixed(2)}
            </span>
          </div>
        );
      }
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          header, .no-print {
            display: none !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-footer-group;
          }
          @page {
            size: landscape;
            margin: 0.5cm;
          }
        }
      `}</style>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30 no-print">
        <div className="max-w-full mx-auto px-3 sm:px-6 py-3 sm:py-4">
          {/* Top Row - Logo, Menu Button, User Info */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg flex items-center justify-center shadow-md">
                <Database className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Stock Management</h1>
                <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">Admin Control Panel</p>
              </div>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Global Search Button */}
              <Button
                onClick={() => setShowGlobalSearch(true)}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 h-8 px-2 sm:px-3"
                data-testid="global-search-btn"
              >
                <Search className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Search</span>
                <kbd className="hidden md:inline ml-2 px-1.5 py-0.5 text-[10px] bg-slate-100 rounded border border-slate-200">⌘K</kbd>
              </Button>
              
              <span className="text-xs sm:text-sm font-semibold text-slate-700">{user.username}</span>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 h-8 px-2 sm:px-3"
              >
                <LogOut className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="hidden p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 mt-3">
            <div className="flex bg-slate-100 rounded-lg p-1 overflow-x-auto">
              {user.role === 'winlife' && (
                <Button
                  data-testid="upload-tab"
                  onClick={() => setActiveTab('upload')}
                  variant={activeTab === 'upload' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'upload' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Upload Stock
                </Button>
              )}
              {user.role === 'winlife' && (
                <Button
                  data-testid="viewsheet-tab"
                  onClick={() => setActiveTab('viewsheet')}
                  variant={activeTab === 'viewsheet' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'viewsheet' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                  View Sheet
                </Button>
              )}
              {hasAccess('dashboard') && (
                <Button
                  data-testid="dashboard-tab"
                  onClick={() => setActiveTab('dashboard')}
                  variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'dashboard' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <LayoutDashboard className="w-4 h-4 mr-1.5" />
                  Dashboard
                </Button>
              )}
              {hasAccess('stock') && (
                <Button
                  data-testid="merged-tab"
                  onClick={() => setActiveTab('merged')}
                  variant={activeTab === 'merged' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'merged' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Database className="w-4 h-4 mr-1.5" />
                  Merged Stock
                </Button>
              )}
              {hasAccess('orders') && (
                <Button
                  data-testid="orders-tab"
                  onClick={() => setActiveTab('orders')}
                  variant={activeTab === 'orders' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'orders' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <ClipboardList className="w-4 h-4 mr-1.5" />
                  Orders
                </Button>
              )}
              {hasAccess('todo') && (
                <Button
                  onClick={() => setActiveTab('todo')}
                  variant={activeTab === 'todo' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'todo' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <CheckSquare className="w-4 h-4 mr-1.5" />
                  To-Do
                </Button>
              )}
              {hasAccess('accounts') && (
                <Button
                  onClick={() => setActiveTab('accounts')}
                  variant={activeTab === 'accounts' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'accounts' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Accounts
                </Button>
              )}
              {hasAccess('product_planning') && (
                <Button
                  data-testid="planning-tab"
                  onClick={() => setActiveTab('planning')}
                  variant={activeTab === 'planning' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'planning' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Lightbulb className="w-4 h-4 mr-1.5" />
                  Planning
                </Button>
              )}
              {hasAccess('pricelist') && (
                <Button
                  data-testid="pricelist-tab"
                  onClick={() => setActiveTab('pricelist')}
                  variant={activeTab === 'pricelist' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'pricelist' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <DollarSign className="w-4 h-4 mr-1.5" />
                  Price List
                </Button>
              )}
              {hasAccess('projects') && (
                <Button
                  data-testid="projects-tab"
                  onClick={() => setActiveTab('projects')}
                  variant={activeTab === 'projects' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'projects' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FolderKanban className="w-4 h-4 mr-1.5" />
                  Projects
                </Button>
              )}
              {hasAccess('analytics') && (
                <Button
                  data-testid="analytics-tab"
                  onClick={() => setActiveTab('analytics')}
                  variant={activeTab === 'analytics' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'analytics' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  Analytics
                </Button>
              )}
              {hasAccess('messaging') && (
                <Button
                  data-testid="messaging-tab"
                  onClick={() => setActiveTab('messaging')}
                  variant={activeTab === 'messaging' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'messaging' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <MessageSquare className="w-4 h-4 mr-1.5" />
                  Messaging
                </Button>
              )}
              {user.role === 'admin' && (
                <Button
                  data-testid="users-tab"
                  onClick={() => setActiveTab('users')}
                  variant={activeTab === 'users' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'users' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  Users
                </Button>
              )}
              {hasAccess('media') && (
                <Button
                  data-testid="media-tab"
                  onClick={() => setActiveTab('media')}
                  variant={activeTab === 'media' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'media' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Image className="w-4 h-4 mr-1.5" />
                  Media
                </Button>
              )}
              {hasAccess('documents') && (
                <Button
                  data-testid="documents-tab"
                  onClick={() => setActiveTab('documents')}
                  variant={activeTab === 'documents' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'documents' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <FolderOpen className="w-4 h-4 mr-1.5" />
                  Documents
                </Button>
              )}
              {(user.role === 'admin' || user.role === 'winlife') && (
                <Button
                  data-testid="activity-tab"
                  onClick={() => setActiveTab('activity')}
                  variant={activeTab === 'activity' ? 'default' : 'ghost'}
                  size="sm"
                  className={activeTab === 'activity' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-200'}
                >
                  <Activity className="w-4 h-4 mr-1.5" />
                  Activity
                </Button>
              )}
            </div>
            {activeTab === 'merged' && (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-40"
                />
                <Button
                  data-testid="toggle-cost-button"
                  onClick={() => setShowCost(!showCost)}
                  variant="outline"
                  size="sm"
                  className="border-slate-300 hover:bg-slate-50"
                >
                  {showCost ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {showCost ? 'Hide' : 'Show'} Cost
                </Button>
                <Button onClick={handlePrint} variant="outline" size="sm" className="border-slate-300 hover:bg-slate-50">
                  <Printer className="w-4 h-4 mr-1.5" />
                  Print
                </Button>
                <Button data-testid="refresh-button" onClick={fetchStocks} variant="outline" size="sm" className="border-slate-300 hover:bg-slate-50">
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Refresh
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg">
            <nav className="grid grid-cols-3 gap-1 p-2">
              {/* WINLIFE Upload Button */}
              {user.role === 'winlife' && (
                <button
                  onClick={() => { setActiveTab('upload'); setMobileMenuOpen(false); }}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'upload' 
                      ? 'bg-teal-600 text-white shadow-md' 
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Upload className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium">Upload</span>
                </button>
              )}
              {/* WINLIFE View Sheet Button */}
              {user.role === 'winlife' && (
                <button
                  onClick={() => { setActiveTab('viewsheet'); setMobileMenuOpen(false); }}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === 'viewsheet' 
                      ? 'bg-indigo-600 text-white shadow-md' 
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium">View Sheet</span>
                </button>
              )}
              {[
                { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard' },
                { id: 'merged', icon: Database, label: 'Stock', permission: 'stock' },
                { id: 'orders', icon: ClipboardList, label: 'Orders', permission: 'orders' },
                { id: 'todo', icon: CheckSquare, label: 'To-Do', permission: 'todo' },
                { id: 'accounts', icon: FileText, label: 'Accounts', permission: 'accounts' },
                { id: 'planning', icon: Lightbulb, label: 'Planning', permission: 'product_planning' },
                { id: 'pricelist', icon: DollarSign, label: 'Price List', permission: 'pricelist' },
                { id: 'projects', icon: FolderKanban, label: 'Projects', permission: 'projects' },
                { id: 'messaging', icon: MessageSquare, label: 'Messaging', permission: 'messaging' },
                { id: 'activity', icon: Activity, label: 'Activity', operatorOnly: true },
              ].filter(tab => tab.adminOnly ? user.role === 'admin' : tab.operatorOnly ? (user.role === 'admin' || user.role === 'winlife') : hasAccess(tab.permission)).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                    activeTab === tab.id 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mb-1" />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
            
            {/* Mobile Action Bar for Merged Stock */}
            {activeTab === 'merged' && (
              <div className="px-3 pb-3 space-y-2">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCost(!showCost)}
                    variant="outline"
                    size="sm"
                    className="flex-1 h-9"
                  >
                    {showCost ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {showCost ? 'Hide' : 'Show'} Cost
                  </Button>
                  <Button onClick={handlePrint} variant="outline" size="sm" className="h-9">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button onClick={fetchStocks} variant="outline" size="sm" className="h-9">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Main Content */}
      <PullToRefresh onRefresh={handleMobileRefresh}>
      <main className="px-2 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 pb-20 lg:pb-8">
        {activeTab === 'merged' && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-slate-200 overflow-hidden print-content">
            {/* Stats Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 no-print">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
                <div>
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Merged Stock Overview</h2>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1">Combined inventory from all locations</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl px-4 sm:px-6 py-2 sm:py-3 border border-white/20">
                  <p className="text-xs sm:text-sm text-slate-300">Total Products</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white" data-testid="total-products">{stocks.length}</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                <Button
                  onClick={handleRemoveDuplicates}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  className="bg-yellow-500/20 border-yellow-400 text-yellow-100 hover:bg-yellow-500/30 text-xs sm:text-sm h-8 sm:h-9"
                >
                  <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Remove </span>Duplicates
                </Button>
                <Button
                  onClick={handleWipeMergedStock}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  className="bg-red-500/20 border-red-400 text-red-100 hover:bg-red-500/30 text-xs sm:text-sm h-8 sm:h-9"
                >
                  <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Wipe & Refresh
                </Button>
                <Button
                  onClick={handleRefreshMergedStock}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  className="bg-blue-500/20 border-blue-400 text-blue-100 hover:bg-blue-500/30 text-xs sm:text-sm h-8 sm:h-9"
                >
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Print Title */}
            <div className="hidden print:block p-4 border-b">
              <h1 className="text-xl font-bold">Merged Stock Report</h1>
              <p className="text-sm text-gray-600">Total Products: {stocks.length}</p>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <svg className="animate-spin h-12 w-12 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-slate-600">Loading stock data...</p>
                </div>
              </div>
            ) : (
              <StockTable 
                data={filteredStocks} 
                columns={columns} 
                onSaveOrderFields={saveOrderFields}
                enableOrderEdit={true}
              />
            )}
          </div>
        )}

        {/* WINLIFE Upload Panel */}
        {activeTab === 'upload' && user.role === 'winlife' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Stock Data</h2>
                <p className="text-slate-300 mt-1">Upload your WINLIFE stock Excel file</p>
              </div>
              <Button 
                onClick={() => setActiveTab('merged')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Stock
              </Button>
            </div>
            <div className="p-8">
              <div className="max-w-2xl mx-auto">
                <div className="bg-slate-50 rounded-xl p-8 border-2 border-dashed border-slate-300">
                  <div className="text-center">
                    <Upload className="w-16 h-16 text-teal-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Upload WINLIFE Stock File</h3>
                    <p className="text-slate-500 mb-6">Select your Excel file (.xlsx, .xls) or CSV file</p>
                    
                    <input
                      id="winlife-file-input"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    
                    <div className="space-y-4">
                      <Button
                        onClick={() => document.getElementById('winlife-file-input').click()}
                        variant="outline"
                        size="lg"
                        className="w-full border-teal-300 text-teal-700 hover:bg-teal-50"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Select File
                      </Button>
                      
                      {uploadFile && (
                        <div className="bg-white rounded-lg p-4 border border-teal-200">
                          <p className="text-sm text-slate-600">Selected file:</p>
                          <p className="font-semibold text-teal-700">{uploadFile.name}</p>
                        </div>
                      )}
                      
                      <Button
                        onClick={handleUpload}
                        disabled={!uploadFile || uploading}
                        size="lg"
                        className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                      >
                        {uploading ? (
                          <>
                            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 mr-2" />
                            Upload & Process
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Expected Columns Info */}
                <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">Expected Excel Columns:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span className="text-blue-700">Product Name</span>
                      <span className="text-blue-400">(Required)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span className="text-blue-700">Available Qty</span>
                      <span className="text-blue-400">(Required)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-300 rounded-full"></span>
                      <span className="text-blue-700">Packing</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-300 rounded-full"></span>
                      <span className="text-blue-700">Manufactured By</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-300 rounded-full"></span>
                      <span className="text-blue-700">Cost</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      <span className="text-blue-700 font-medium">Expiry Date</span>
                      <span className="text-orange-500">(Important!)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WINLIFE View Sheet Panel */}
        {activeTab === 'viewsheet' && user.role === 'winlife' && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center">
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WINLIFE Stock Sheet</h2>
                <p className="text-xs sm:text-sm text-indigo-200 mt-1">Your uploaded stock inventory</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowCost(!showCost)}
                  variant="outline"
                  size="sm"
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                >
                  {showCost ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  {showCost ? 'Hide' : 'Show'} Cost
                </Button>
                <Button 
                  onClick={fetchWinlifeStocks}
                  disabled={winlifeLoading}
                  variant="outline"
                  size="sm"
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${winlifeLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  onClick={() => setActiveTab('upload')}
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Upload
                </Button>
              </div>
            </div>
            
            {/* Stats Header */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-4 sm:px-6 lg:px-8 py-3 border-b border-indigo-100">
              <div className="flex justify-between items-center">
                <p className="text-sm text-indigo-700">
                  Total Products: <span className="font-bold">{winlifeStocks.length}</span>
                </p>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-48"
                />
              </div>
            </div>

            {/* Table */}
            {winlifeLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-indigo-600">Loading WINLIFE stock data...</p>
                </div>
              </div>
            ) : (
              <StockTable 
                data={winlifeStocks.filter(stock => {
                  if (!searchTerm) return true;
                  const search = searchTerm.toLowerCase();
                  return (
                    stock.product_name?.toLowerCase().includes(search) ||
                    stock.manufactured_by?.toLowerCase().includes(search) ||
                    stock.packing?.toLowerCase().includes(search)
                  );
                })} 
                columns={winlifeColumns}
                enableOrderEdit={false}
              />
            )}
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dashboard</h2>
              <p className="text-slate-300 mt-1">Overview of key metrics and insights</p>
            </div>
            <div className="p-6">
              <Dashboard user={user} onNavigate={setActiveTab} />
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Orders Management</h2>
                <p className="text-slate-300 mt-1">Track and manage product orders</p>
              </div>
              <Button 
                onClick={() => setActiveTab('dashboard')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="p-6">
              <Orders user={user} />
            </div>
          </div>
        )}

        {activeTab === 'todo' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>To-Do List</h2>
                <p className="text-slate-300 mt-1">Manage tasks and assignments</p>
              </div>
              <Button 
                onClick={() => setActiveTab('dashboard')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="p-6">
              <ToDo user={user} />
            </div>
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Accounts</h2>
                <p className="text-slate-300 mt-1">Bank statements and transaction ledger</p>
              </div>
              <Button 
                onClick={() => setActiveTab('dashboard')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="p-6">
              <Accounts user={user} />
            </div>
          </div>
        )}

        {activeTab === 'planning' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Product Planning</h2>
                <p className="text-slate-300 mt-1">Plan new products with detailed specifications and supplier orders</p>
              </div>
              <Button 
                onClick={() => setActiveTab('dashboard')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="p-6">
              <ProductPlanning user={user} />
            </div>
          </div>
        )}

        {activeTab === 'pricelist' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <PriceList user={user} onBackToDashboard={() => setActiveTab('dashboard')} />
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <NewProjects user={user} onBackToDashboard={() => setActiveTab('dashboard')} />
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden p-6">
            <Analytics user={user} onBackToDashboard={() => setActiveTab('dashboard')} />
          </div>
        )}

        {activeTab === 'messaging' && hasAccess('messaging') && (
          <Messaging user={user} onBack={() => setActiveTab('dashboard')} />
        )}

        {activeTab === 'users' && user.role === 'admin' && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>User Management</h2>
                <p className="text-slate-300 mt-1">Manage users, permissions and monitor activity logs</p>
              </div>
              <Button 
                onClick={() => setActiveTab('dashboard')}
                className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="p-6">
              <UserManagement user={user} />
            </div>
          </div>
        )}

        {activeTab === 'media' && hasAccess('media') && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <MediaLibrary user={user} />
          </div>
        )}

        {activeTab === 'documents' && hasAccess('documents') && (
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <DocumentLibrary user={user} />
          </div>
        )}

        {activeTab === 'activity' && (user.role === 'admin' || user.role === 'winlife') && (
          <ActivityFeed user={user} />
        )}
      </main>
      </PullToRefresh>

      {/* Chat Window */}
      <ChatWindow user={user} />
      
      {/* Quick Actions FAB (Mobile only) */}
      <QuickActions onAction={handleQuickAction} />
      
      {/* Keyboard Shortcuts (Desktop only) */}
      <KeyboardShortcuts onShortcut={handleShortcut} />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        hasAccess={hasAccess}
        showMoreMenu={showMoreMenu}
        setShowMoreMenu={setShowMoreMenu}
      />

      {/* Global Search Modal */}
      <GlobalSearch 
        user={user}
        isOpen={showGlobalSearch}
        onClose={() => setShowGlobalSearch(false)}
        onNavigate={handleSearchNavigate}
      />
    </div>
  );
};

export default AdminDashboard;
